"use client";

import { Activity, ArrowUpRight, Dna, HeartPulse, ScanLine, Sparkles, Watch } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const services = [{
    icon: HeartPulse,
    name: "OTA Revenue Management",
    description: "Strategic optimization of your OTA listings to maximize visibility, conversion rates, and overall revenue performance.",
    colSpan: "lg:col-span-5",
    offset: "lg:mt-0"
  }, {
    icon: Dna,
    name: "Hospitality Tech Implementation",
    description: "Expert guidance on selecting and integrating the right tech stack to streamline operations and boost profitability.",
    colSpan: "lg:col-span-6 lg:col-start-7",
    offset: "lg:mt-20"
  }, {
    icon: ScanLine,
    name: "Revenue Audits",
    description: "Comprehensive analysis of your current revenue streams to identify hidden opportunities and performance gaps.",
    colSpan: "lg:col-span-6",
    offset: "lg:-mt-8"
  }, {
    icon: Activity,
    name: "Direct Booking Growth",
    description: "Proven strategies to shift bookings from high-commission channels to your own direct website platform.",
    colSpan: "lg:col-span-5 lg:col-start-8",
    offset: "lg:mt-12"
  }, {
    icon: Watch,
    name: "Dynamic Pricing Strategy",
    description: "Real-time rate adjustments based on market demand, competitor analysis, and historical booking data.",
    colSpan: "lg:col-span-5 lg:col-start-2",
    offset: "lg:mt-8"
  }, {
    icon: Sparkles,
    name: "Rate Parity Control",
    description: "Ensuring consistent pricing across all distribution channels to protect your brand integrity and margins.",
    colSpan: "lg:col-span-6",
    offset: "lg:-mt-4"
  }];
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section data-section-id="4579" id="servicenavigationmenu" className="bg-background text-foreground py-24 md:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-8">
            <p className="font-default text-xs uppercase tracking-[0.25em] mb-8 text-chart-3">
              — 01 / Our Expertise
            </p>
            <h1 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Revenue solutions,
              <br />
              <em className="italic text-primary">
                data-driven precision
              </em>
            </h1>
          </div>
          <div className="lg:col-span-4 lg:pt-6">
            <p className="text-base leading-relaxed text-secondary font-default">
              We provide tailored revenue management strategies designed to maximize your hotel's profitability through expert analysis and modern technology.
            </p>
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <ServiceCard key={i} data-index={i} icon={Icon} name={service.name} description={service.description} colSpan={service.colSpan} offset={service.offset} delay={i * 100} />;
        })}
        </div>

        {}
        <div ref={footerRef} className={`mt-24 md:mt-32 text-center transition-all duration-1000 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Link to="/OurServicesPage" className="inline-block font-default italic text-xl md:text-2xl text-foreground hover:opacity-70 transition-opacity duration-500">
            <span className="border-b pb-1 border-chart-3">
              View all revenue services
            </span>
          </Link>
        </div>
      </div>
    </section>;
}
type ServiceCardProps = {
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  name: string;
  description: string;
  colSpan: string;
  offset: string;
  delay: number;
  "data-index": number;
};
function ServiceCard({
  icon: Icon,
  name,
  description,
  colSpan,
  offset,
  delay,
  ...rest
}: ServiceCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <div {...rest} ref={ref} className={`${colSpan} ${offset} group`} style={{
    transitionDelay: `${delay}ms`
  }}>
      <Card className="relative h-full p-8 md:p-10 border bg-card transition-all duration-500 ease-out hover:-translate-y-0.5 shadow-md rounded-lg">
        <div className="absolute inset-0 pointer-events-none transition-colors duration-500 rounded-lg" />
        <style>{`
          .group:hover > [data-card-inner] {
            border-color: var(--chart-3) !important;
          }
        `}</style>
        <div data-card-inner className="absolute inset-0 border transition-colors duration-500 pointer-events-none rounded-lg" style={{
        borderColor: "transparent"
      }} />

        <div className="relative flex flex-col h-full min-h-[260px]">
          {}
          <div className="mb-10 transition-transform duration-500 group-hover:rotate-[5deg] text-primary">
            <Icon className="w-7 h-7" strokeWidth={1.25} />
          </div>

          {}
          <h3 className={`font-default text-2xl md:text-3xl leading-tight tracking-tight text-card-foreground mb-4 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: `${delay + 100}ms`
        }}>
            {name}
          </h3>

          {}
          <p className={`text-sm md:text-base leading-relaxed mb-10 transition-all duration-700 ease-out flex-grow text-secondary font-default ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: `${delay + 200}ms`
        }}>
            {description}
          </p>

          {}
          <div className={`mt-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: `${delay + 300}ms`
        }}>
            <Link to="/OurServicesPage" className="inline-flex items-center gap-2 font-default text-xs uppercase tracking-[0.2em] text-foreground hover:opacity-60 transition-opacity duration-300">
              <span>Learn more</span>
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </Card>
    </div>;
}